// Strict PII & Sensitive Data Sanitization for Client Logs
const SENSITIVE_KEY_PATTERNS = [
  /phone/i,
  /email/i,
  /password/i,
  /passwd/i,
  /ssn/i,
  /secret/i,
  /pin/i,
  /cvv/i,
  /token/i,
  /otp/i,
  /passcode/i,
  /api[_-]?key/i,
  /verification[_-]?id/i,
  /session[_-]?info/i,
  /routing.*number/i,
  /account.*number/i,
  /card.*number/i,
];

const SAFE_EXEMPT_KEYS = new Set([
  'has_phone_number',
  'has_user_phone',
  'has_push_token',
  'has_verification_id',
  'has_display_name',
  'is_phone_contact',
  'has_connected_bank',
  'connected_bank_count',
  'has_connected_cards',
  'has_gmail_sync',
  'profile_completed',
  'is_plus',
  'subscription_tier',
  'subscription_status',
  'fingerprint_visitor_id',
  'fingerprint_request_id',
  'keychain_device_id',
  'visitor_id',
  'device_id',
]);

export const redactPiiString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/\bExpo(?:nent)?PushToken\[[^\]]+\]/gi, '[REDACTED_PUSH_TOKEN]')
    .replace(/(bearer\s+)[a-zA-Z0-9_\-\.]+/gi, '$1[REDACTED_TOKEN]')
    .replace(/\beyJ[a-zA-Z0-9_\-]{10,}\.[a-zA-Z0-9_\-]{10,}\.[a-zA-Z0-9_\-]+\b/g, '[REDACTED_JWT]')
    .replace(/(["']?([a-zA-Z0-9_]*(?:password|passwd|secret|token|pin|cvv|ssn|otp|passcode)|api[_-]?key|verification[_-]?id|session[_-]?info|routing[_-]?number|account[_-]?number|card[_-]?number)["']?\s*[:=]\s*)("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\[REDACTED_[A-Z_]+\]|[^"'\s,;}\]]+)/gi, (match, prefix, rawKey, rawVal) => {
      if (String(rawVal || '').includes('[REDACTED_')) {
        return match;
      }
      const cleanKey = String(rawKey || '').replace(/^["']|["']$/g, '');
      const lowerKey = cleanKey.toLowerCase();
      const unquotedVal = String(rawVal || '').replace(/^["']|["']$/g, '').toLowerCase();
      if (
        SAFE_EXEMPT_KEYS.has(cleanKey) ||
        SAFE_EXEMPT_KEYS.has(lowerKey) ||
        unquotedVal === 'true' ||
        unquotedVal === 'false'
      ) {
        return match;
      }
      if (prefix.trim().startsWith('"') || prefix.trim().startsWith("'")) {
        return prefix + '"[REDACTED_SECRET]"';
      }
      return cleanKey + ': "[REDACTED_SECRET]"';
    })
    .replace(/data:[^;\s]+;base64,[^\s"'\)]+/g, '[REDACTED_IMAGE_DATA]')
    .replace(/(?:\+[1-9]\d{9,14})\b/g, '[REDACTED_PHONE]')
    .replace(/(?:\+?1\s*[-.]?\s*)?\(?([0-9]{3})\)?\s*[-.]?\s*([0-9]{3})\s*[-.]?\s*([0-9]{4})\b/g, '[REDACTED_PHONE]')
    .replace(/\b[1-9]\d{9,14}\b/g, '[REDACTED_PHONE]')
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[REDACTED_EMAIL]')
    .replace(/\b(?:\d{4}[- ]?){3}\d{4}\b/g, '[REDACTED_CARD]');
};

const resolveUniqueSanitizedKey = (target, rawKey) => {
  const baseKey = redactPiiString(String(rawKey || ''));
  if (!Object.prototype.hasOwnProperty.call(target, baseKey)) {
    return baseKey;
  }
  let suffix = 2;
  while (Object.prototype.hasOwnProperty.call(target, `${baseKey}_${suffix}`)) {
    suffix += 1;
  }
  return `${baseKey}_${suffix}`;
};

export const sanitizeLogArg = (arg, seen = new WeakSet()) => {
  if (typeof arg === 'string') {
    return redactPiiString(arg);
  }
  if (typeof arg === 'number' || typeof arg === 'boolean' || arg == null) {
    return arg;
  }
  if (arg instanceof Error) {
    const sanitizedError = new Error(redactPiiString(arg.message));
    sanitizedError.name = arg.name;
    if (arg.stack) {
      sanitizedError.stack = redactPiiString(arg.stack);
    }
    return sanitizedError;
  }
  if (typeof arg === 'object') {
    if (seen.has(arg)) {
      return '[Circular]';
    }
    seen.add(arg);

    if (Array.isArray(arg)) {
      return arg.map((item) => sanitizeLogArg(item, seen));
    }

    const sanitizedObj = {};
    for (const [key, value] of Object.entries(arg)) {
      if (SAFE_EXEMPT_KEYS.has(key)) {
        sanitizedObj[key] = sanitizeLogArg(value, seen);
        continue;
      }
      const isSensitive = SENSITIVE_KEY_PATTERNS.some((p) => p.test(key));
      if (isSensitive) {
        const safeKey = resolveUniqueSanitizedKey(sanitizedObj, key);
        if (typeof value === 'boolean') {
          sanitizedObj[safeKey] = value;
        } else {
          sanitizedObj[safeKey] = '[REDACTED]';
        }
        continue;
      }
      const safeKey = resolveUniqueSanitizedKey(sanitizedObj, key);
      sanitizedObj[safeKey] = sanitizeLogArg(value, seen);
    }
    return sanitizedObj;
  }
  return String(arg);
};

export const sanitizeLogArgs = (args) => {
  const seen = new WeakSet();
  return args.map((arg) => sanitizeLogArg(arg, seen));
};

export const serializeConsoleArg = (arg) => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === 'string') return arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (!arg) return '';
  try {
    return JSON.stringify(arg);
  } catch {
    return Object.prototype.toString.call(arg);
  }
};

export const sanitizeSentryLogEvent = (log) => {
  if (!log || typeof log !== 'object') return log;
  if (typeof log.message === 'string') {
    log.message = redactPiiString(log.message);
  }
  if (typeof log.body === 'string') {
    log.body = redactPiiString(log.body);
  }
  if (log.attributes && typeof log.attributes === 'object') {
    const cleanAttributes = {};
    for (const [key, val] of Object.entries(log.attributes)) {
      if (key === 'user.email' || key === 'user.name') {
        continue;
      }
      if (key.startsWith('sentry.')) {
        cleanAttributes[key] = sanitizeLogArg(val);
        continue;
      }
      if (SAFE_EXEMPT_KEYS.has(key)) {
        cleanAttributes[key] = sanitizeLogArg(val);
        continue;
      }
      const isSensitive = SENSITIVE_KEY_PATTERNS.some((p) => p.test(key));
      if (isSensitive) {
        const safeKey = resolveUniqueSanitizedKey(cleanAttributes, key);
        if (typeof val === 'boolean') {
          cleanAttributes[safeKey] = val;
        } else {
          cleanAttributes[safeKey] = '[REDACTED]';
        }
        continue;
      }
      const safeKey = resolveUniqueSanitizedKey(cleanAttributes, key);
      cleanAttributes[safeKey] = sanitizeLogArg(val);
    }
    log.attributes = cleanAttributes;
  }
  return log;
};

export const SENTRY_TRACE_PROPAGATION_TARGETS = [
  /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/i,
  /^https:\/\/[a-z0-9-]+-clero-b49eb\.cloudfunctions\.net(?:\/|$)/i,
  /^https:\/\/(?:www\.)?clero\.money(?:\/|$)/i,
  /^https:\/\/clero-b49eb\.(?:web\.app|firebaseapp\.com)(?:\/|$)/i,
];

const ROUTE_TOKEN_TEMPLATES = [
  [/(\/api\/og\/pay\/)[^/?#]+\.png\b/gi, '$1:token.png'],
  [/(\/api\/og\/pay\/)(?!:token\.png\b)[^/?#]+/gi, '$1:token'],
  [/(?<!\/api\/og)(\/pay\/)[^/?#]+/gi, '$1:token'],
  [/(\/split\/)[^/?#]+/gi, '$1:token'],
  [/(\/group-invite\/)[^/?#]+/gi, '$1:token'],
  [/(\/claim\/)[^/?#]+/gi, '$1:token'],
  [/(\/o\/)[^/?#]+/gi, '$1:id'],
  [/(\/s\/)(?!new(?:\/|$|\?|#))[^/?#]+/gi, '$1:id'],
  [/(\/return\/)[^/?#]+/gi, '$1:sessionId'],
  [/(\/t\/)[^/?#]+/gi, '$1:code'],
];

export const sanitizeRoutePath = (rawPath) => {
  const pathStr = String(rawPath || '').trim();
  if (!pathStr) return '';
  const withoutQueryOrHash = pathStr.split(/[?#]/)[0] || pathStr;
  let templated = withoutQueryOrHash;
  for (const [pattern, replacement] of ROUTE_TOKEN_TEMPLATES) {
    templated = templated.replace(pattern, replacement);
  }
  return redactPiiString(templated);
};

export const sanitizeUrlForTelemetry = (rawUrl) => {
  const str = String(rawUrl || '').trim();
  if (!str) return '';
  if (/^[A-Z]+\s+\S+$/i.test(str)) {
    const spaceIdx = str.indexOf(' ');
    const method = str.slice(0, spaceIdx + 1);
    const rest = str.slice(spaceIdx + 1);
    return `${method}${sanitizeUrlForTelemetry(rest)}`;
  }
  try {
    const hasOrigin = /^https?:\/\//i.test(str);
    const parsed = new URL(str, 'https://clero.money');
    const cleanPath = sanitizeRoutePath(parsed.pathname);
    return hasOrigin ? `${parsed.origin}${cleanPath}` : cleanPath;
  } catch {
    return sanitizeRoutePath(str);
  }
};

export const sanitizeSentrySpanData = (span) => {
  if (!span || typeof span !== 'object') return span;
  if (typeof span.description === 'string') {
    span.description = redactPiiString(sanitizeUrlForTelemetry(span.description));
  }
  if (typeof span.name === 'string') {
    span.name = redactPiiString(sanitizeUrlForTelemetry(span.name));
  }
  const sanitizeFieldMap = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const cleaned = sanitizeLogArg(obj);
    for (const [key, val] of Object.entries(cleaned)) {
      const lowerKey = key.toLowerCase();
      if (
        lowerKey === 'http.query' ||
        lowerKey === 'http.fragment' ||
        lowerKey === 'url.search' ||
        lowerKey === 'url.hash' ||
        lowerKey === 'url.query' ||
        lowerKey === 'url.fragment' ||
        lowerKey === 'query' ||
        lowerKey === 'query_string'
      ) {
        delete cleaned[key];
        continue;
      }
      if (
        typeof val === 'string' &&
        (lowerKey.includes('url') ||
          lowerKey.includes('path') ||
          lowerKey.includes('route') ||
          lowerKey === 'http.target' ||
          val.includes('://') ||
          val.startsWith('/'))
      ) {
        cleaned[key] = sanitizeUrlForTelemetry(val);
      }
    }
    return cleaned;
  };
  if (span.data && typeof span.data === 'object') {
    span.data = sanitizeFieldMap(span.data);
  }
  if (span.attributes && typeof span.attributes === 'object') {
    span.attributes = sanitizeFieldMap(span.attributes);
  }
  return span;
};

const sanitizeRequestHeaders = (headersObj) => {
  if (!headersObj || typeof headersObj !== 'object' || Array.isArray(headersObj)) {
    return headersObj;
  }
  const headers = sanitizeLogArg(headersObj);
  for (const [key, val] of Object.entries(headers)) {
    if (/^(referer|referrer)$/i.test(key)) {
      if (typeof val === 'string') {
        headers[key] = sanitizeUrlForTelemetry(val);
      } else {
        delete headers[key];
      }
    }
  }
  return headers;
};

const sanitizeTagValue = (key, val) => {
  if (typeof val !== 'string') return val;
  const lowerKey = String(key || '').toLowerCase();
  if (
    lowerKey === 'route' ||
    lowerKey.includes('url') ||
    lowerKey.includes('path') ||
    val.startsWith('/') ||
    /^https?:\/\//i.test(val)
  ) {
    return sanitizeUrlForTelemetry(val);
  }
  return redactPiiString(val);
};

export const sanitizeSentryTransactionEvent = (event) => {
  if (!event || typeof event !== 'object') return event;
  if (typeof event.transaction === 'string') {
    event.transaction = sanitizeUrlForTelemetry(event.transaction);
  }
  if (event.request && typeof event.request === 'object') {
    if (typeof event.request.url === 'string') {
      event.request.url = sanitizeUrlForTelemetry(event.request.url);
    }
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers && typeof event.request.headers === 'object') {
      event.request.headers = sanitizeRequestHeaders(event.request.headers);
    }
  }
  if (event.contexts?.trace && typeof event.contexts.trace === 'object') {
    sanitizeSentrySpanData(event.contexts.trace);
  }
  if (Array.isArray(event.spans)) {
    event.spans = event.spans.map((span) => sanitizeSentrySpanData(span));
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((b) => sanitizeSentryBreadcrumb(b));
  }
  if (event.tags && typeof event.tags === 'object') {
    for (const [k, v] of Object.entries(event.tags)) {
      if (typeof v === 'string') {
        event.tags[k] = sanitizeTagValue(k, v);
      }
    }
  }
  if (
    event.sdkProcessingMetadata?.dynamicSamplingContext &&
    typeof event.sdkProcessingMetadata.dynamicSamplingContext === 'object'
  ) {
    const dsc = event.sdkProcessingMetadata.dynamicSamplingContext;
    if (typeof dsc.transaction === 'string') {
      dsc.transaction = sanitizeUrlForTelemetry(dsc.transaction);
    }
  }
  return event;
};

export const createSentryTransactionPrivacyIntegration = () => ({
  name: 'CleroTransactionPrivacy',
  setup(client) {
    if (!client || typeof client.on !== 'function') return;
    client.on('createDsc', (dsc) => {
      if (dsc && typeof dsc.transaction === 'string') {
        dsc.transaction = sanitizeUrlForTelemetry(dsc.transaction);
      }
    });
    client.on('beforeEnvelope', (envelope) => {
      const header = Array.isArray(envelope) ? envelope[0] : null;
      if (header?.trace && typeof header.trace.transaction === 'string') {
        header.trace.transaction = sanitizeUrlForTelemetry(header.trace.transaction);
      }
    });
  },
});

export const sanitizeSpanAttributes = (attributes) => {
  if (!attributes || typeof attributes !== 'object') return undefined;
  const sanitized = sanitizeLogArg(attributes);
  if (!sanitized || typeof sanitized !== 'object' || Array.isArray(sanitized)) {
    return undefined;
  }
  const result = {};
  for (const [key, val] of Object.entries(sanitized)) {
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      result[key] = val;
    } else if (val != null) {
      result[key] = redactPiiString(serializeConsoleArg(val));
    }
  }
  return result;
};

export const sanitizeSentryBreadcrumb = (breadcrumb) => {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactPiiString(breadcrumb.message);
  }
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    const cleaned = sanitizeLogArg(breadcrumb.data);
    if (cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned)) {
      for (const [key, val] of Object.entries(cleaned)) {
        const lowerKey = key.toLowerCase();
        if (
          lowerKey === 'http.query' ||
          lowerKey === 'http.fragment' ||
          lowerKey === 'url.search' ||
          lowerKey === 'url.hash' ||
          lowerKey === 'url.query' ||
          lowerKey === 'url.fragment' ||
          lowerKey === 'query' ||
          lowerKey === 'query_string'
        ) {
          delete cleaned[key];
          continue;
        }
        if (
          typeof val === 'string' &&
          (lowerKey.includes('url') ||
            lowerKey.includes('path') ||
            lowerKey.includes('route') ||
            lowerKey === 'http.target' ||
            val.includes('://') ||
            val.startsWith('/'))
        ) {
          cleaned[key] = sanitizeUrlForTelemetry(val);
        }
      }
    }
    breadcrumb.data = cleaned;
  }
  return breadcrumb;
};

export const sanitizeSentryErrorEvent = (event) => {
  if (!event || typeof event !== 'object') return event;
  if (typeof event.message === 'string') {
    event.message = redactPiiString(event.message);
  }
  if (event.request && typeof event.request === 'object') {
    if (typeof event.request.url === 'string') {
      event.request.url = sanitizeUrlForTelemetry(event.request.url);
    }
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers && typeof event.request.headers === 'object') {
      event.request.headers = sanitizeRequestHeaders(event.request.headers);
    }
  }
  if (Array.isArray(event.exception?.values)) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: typeof ex?.value === 'string' ? redactPiiString(ex.value) : ex?.value,
    }));
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((b) => sanitizeSentryBreadcrumb(b));
  }
  if (event.tags && typeof event.tags === 'object') {
    for (const [k, v] of Object.entries(event.tags)) {
      if (typeof v === 'string') {
        event.tags[k] = sanitizeTagValue(k, v);
      }
    }
  }
  return event;
};
